/* eslint-disable import/no-unresolved */

import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import styled from 'styled-components';
import FlexWrapper from 'TMComponents/FlexWrapper';
import { breakpoints } from 'TMConstants';
import { node, oneOf } from 'prop-types';

const Embla = styled.section`
    position: relative;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    --slide-height: 100%;
    --slide-spacing: 0px;

    --slide-size: 100%;
    @media (min-width: ${breakpoints.mobile}px) {
        --slide-size: max-content;
    }
`;

const EmblaViewport = styled.div`
    @media (min-width: ${breakpoints.tabletDesktop}px) {
        overflow: hidden;
    }
    position: relative;
    width: 100%;
    max-width: 1070px;
`;

const EmblaContainer = styled.div`
    backface-visibility: hidden;
    display: flex;
    justify-content: space-between;
    position: relative;
    touch-action: pan-y pinch-zoom;
`;

const EmblaSlide = styled.div`
    flex: 0 0 var(--slide-size);
    min-height: 100%;
    min-width: 0;
    margin: 4px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
`;

const CircleWrapper = styled(FlexWrapper)`
    padding: 20px;
    ${({ numberOfSlides }) => {
        if (numberOfSlides === 1) return 'display:none;';
        if (numberOfSlides < 4) {
            /* istanbul ignore next */
            return `
                @media (min-width: ${breakpoints.tabletDesktop}px) {
                    display:none;
                }
        `;
        }

        return '';
    }}
`;

const Circle = styled.div`
    background-color: ${({ theme }) => theme.colorSystem.primary[1]};

    ${({ theme, indicatorColor }) =>
        indicatorColor === 'light' &&
        `background-color: ${theme.colorSystem.primary[3]};`}

    &.selected {
        background-color: ${({ theme }) => theme.colorSystem.secondary[3]};
    }

    border-radius: 50%;
    height: 16px;
    margin-right: 8px;
    width: 16px;

    &:last-child {
        margin-right: none;
    }
`;

export const useSelectedSnapDisplay = (emblaApi) => {
    const [selectedSnap, setSelectedSnap] = useState(0);
    const [snapCount, setSnapCount] = useState(0);

    const updateScrollSnapState = useCallback((api) => {
        setSnapCount(api.scrollSnapList().length);
        setSelectedSnap(api.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;
        updateScrollSnapState(emblaApi);
        emblaApi.on('select', updateScrollSnapState);
        emblaApi.on('reInit', updateScrollSnapState);
    }, [emblaApi, updateScrollSnapState]);

    return {
        selectedSnap,
        snapCount
    };
};

const Carousel = ({ children, indicatorColor = 'dark', ...rest }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel(
        { align: 'start', loop: true },
        [Autoplay()]
    );
    const { selectedSnap } = useSelectedSnapDisplay(emblaApi);

    return (
        <Embla {...rest}>
            <EmblaViewport ref={emblaRef}>
                <EmblaContainer>
                    {children.map((child) => (
                        <EmblaSlide key={child.key}>{child}</EmblaSlide>
                    ))}
                </EmblaContainer>
                <CircleWrapper
                    justify="center"
                    numberOfSlides={children.length}>
                    {children.map((_, i) => (
                        <Circle
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            className={i === selectedSnap && 'selected'}
                            data-testid={`circle-${i}`}
                            indicatorColor={indicatorColor}
                        />
                    ))}
                </CircleWrapper>
            </EmblaViewport>
        </Embla>
    );
};

export default Carousel;

Carousel.propTypes = {
    indicatorColor: oneOf(['dark', 'light']),
    children: node.isRequired
};
